import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Building, Check, ImageUp, MapPin, Settings, Trophy } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button, Surface, Text } from '@/components/flup';
import { useAppSelector } from '@/hooks/useReduxHooks';
import { RootState } from '@/store';

/**
 * The onboarding rail.
 *
 * Five steps is enough that a vendor mid-way through needs to know how much is
 * left, so the rail shows all of them with the current one marked — not just a
 * bare percentage. On a phone there is no room for the full list, so it
 * collapses to the same information as one line plus a bar.
 */

export type ProgressStep =
  | 'turf-basic'
  | 'turf-address'
  | 'turf-categories'
  | 'turf-sports'
  | 'turf-images';

interface VenueOnboardingProgressProps {
  currentStep: ProgressStep;
}

const STEPS = [
  { id: 'turf-basic', title: 'Venue basics', description: 'Name, details and amenities', icon: Building },
  { id: 'turf-address', title: 'Location', description: 'Address and map pin', icon: MapPin },
  { id: 'turf-categories', title: 'Sports', description: 'What can be played here', icon: Trophy },
  { id: 'turf-sports', title: 'Courts & pricing', description: 'Courts, timings and rates', icon: Settings },
  { id: 'turf-images', title: 'Photos', description: 'How your venue is shown', icon: ImageUp },
] as const;

export const VenueOnboardingProgress: React.FC<VenueOnboardingProgressProps> = ({ currentStep }) => {
  const navigate = useNavigate();
  const { currentOnboardVenueDetails } = useAppSelector((state: RootState) => state.turf);

  const currentIndex = STEPS.findIndex((s) => s.id === currentStep);
  const steps = STEPS.map((step, index) => ({ ...step, completed: index < currentIndex }));

  const completedSteps = steps.filter((s) => s.completed).length;
  const venueName = currentOnboardVenueDetails?.payload?.name;

  // The venue list shows the API's own onboarding percentage, so show the same
  // number here rather than a second, step-derived one that disagrees with it
  // (a venue the API calls 75% done was reading 60% on this rail).
  const apiPercentage = currentOnboardVenueDetails?.payload?.venueOnBoardingPercentage;
  const percentage =
    typeof apiPercentage === 'number'
      ? Math.round(apiPercentage)
      : Math.round((completedSteps / steps.length) * 100);

  const bar = (
    <div>
      <div className="flex items-center justify-between mb-2">
        <Text variant="caption" tone="secondary" as="span">
          Setup progress
        </Text>
        <Text variant="labelSemi" as="span" className="nums">
          {percentage}%
        </Text>
      </div>
      <div className="h-1.5 rounded-pill bg-flup-sunken overflow-hidden">
        <span
          style={{ width: `${percentage}%` }}
          className="block h-full rounded-pill bg-flup-brand transition-[width] duration-500"
        />
      </div>
      <Text variant="micro" tone="tertiary" as="p" className="mt-2">
        Step {Math.max(currentIndex, 0) + 1} of {steps.length}
      </Text>
    </div>
  );

  return (
    <>
      {/* Phone: the same facts, one line high. */}
      <Surface padding="p-4" className="lg:hidden">
        <div className="flex items-center gap-3 mb-3">
          <Button
            variant="ghost"
            size="sm"
            icon={<ArrowLeft className="w-4 h-4" />}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
          <div className="min-w-0">
            <Text variant="labelSemi" as="p" lines={1}>
              {venueName || 'New venue'}
            </Text>
            <Text variant="micro" tone="tertiary" as="p">
              {steps[currentIndex]?.title}
            </Text>
          </div>
        </div>
        {bar}
      </Surface>

      {/* Desktop: the full rail, sticky beside the form. */}
      <Surface padding="p-5" className="hidden lg:block w-72 shrink-0 self-start sticky top-20">
        <div className="flex items-center gap-2 mb-4">
          <Button
            variant="ghost"
            size="sm"
            icon={<ArrowLeft className="w-4 h-4" />}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
        </div>

        <Text variant="h3" as="h2" lines={1}>
          {venueName || 'New venue'}
        </Text>
        <Text variant="caption" tone="tertiary" as="p" className="mt-0.5 mb-5">
          Venue setup
        </Text>

        <ol className="space-y-1">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = step.id === currentStep;
            const last = index === steps.length - 1;

            return (
              <li key={step.id} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <span
                    className={cn(
                      'grid place-items-center w-8 h-8 rounded-pill shrink-0 transition-colors',
                      step.completed
                        ? 'bg-flup-green text-white'
                        : isActive
                          ? 'bg-flup-brand-soft text-flup-brand-ink ring-2 ring-flup-brand'
                          : 'bg-flup-surface-alt text-flup-ink-3',
                    )}
                  >
                    {step.completed ? (
                      <Check className="w-4 h-4" aria-hidden />
                    ) : (
                      <Icon className="w-4 h-4" aria-hidden />
                    )}
                  </span>
                  {!last ? (
                    <span
                      className={cn(
                        'w-0.5 flex-1 min-h-[24px] my-1 rounded-pill',
                        step.completed ? 'bg-flup-green' : 'bg-flup-hairline',
                      )}
                    />
                  ) : null}
                </div>

                <div className={cn('min-w-0 pb-4', last && 'pb-0')}>
                  <Text
                    variant="labelSemi"
                    as="p"
                    tone={step.completed || isActive ? 'primary' : 'tertiary'}
                  >
                    {step.title}
                  </Text>
                  <Text
                    variant="micro"
                    as="p"
                    tone={step.completed ? 'green' : 'tertiary'}
                    className="mt-0.5"
                  >
                    {step.completed ? 'Done' : step.description}
                  </Text>
                </div>
              </li>
            );
          })}
        </ol>

        <div className="mt-5 pt-5 border-t border-flup-hairline">{bar}</div>
      </Surface>
    </>
  );
};

export default VenueOnboardingProgress;
